import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, LinearProgress, FormControl, Select, MenuItem, InputLabel, FormControlLabel, RadioGroup, Radio, Chip, TextField, Avatar, Typography } from "@material-ui/core";
import { getBookingRules, AudienceGroup, BookingRules, BookingType, InviteableUser, CreateBookingParams, Booking, BookingOptions, patchBooking, BookingFundsRecipients, patchFundsRecipients } from "api/bookings";
import moment from "moment";
import { useSnackbar } from "notistack";
import { GridTextField, AudienceSelect, NumberPicker, DateSelect, ClubSelect } from "components";
import { getAdminMemberCount } from "api/admin/members";
import { Club } from "api/clubs";
import { UserRole } from "api/memberships";
import { Autocomplete } from "@material-ui/lab";
import CurrencyTextField from "@unicef/material-ui-currency-textfield";
import config from "config";
import uI18n from "utils/i18n";
import i18n from "../i18n";
import StartDatePicker from "../CreateBookingDialog/StartDatePicker";
import DurationPicker from "../CreateBookingDialog/DurationPicker";
import ParticipantsPicker from "../CreateBookingDialog/ParticipantsPicker";

// prop types
interface EditBookingDialogProps {
    booking?: Booking,
    options?: BookingOptions,
    show: boolean,
    onCancel: () => void,
    onClose: () => void,
    onFetchBookings: () => void,
    club: Club | null,
    getCompanyBookings: (clubId: string) => void,
    fetchBooking: (bookingId: string) => void,
}

// edit booking dialog
const EditBookingDialog = ({ booking, options, show, club, onCancel, onClose, onFetchBookings, getCompanyBookings, fetchBooking }: EditBookingDialogProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.associated?.message ? error.associated.message : `${error.description} (${error.type})`}`,
        { variant: "error" },
    );
    const displayCustomError = (error: any) => enqueueSnackbar(
        `${i18n.invalidOrForbiddenParticipantsError}`,
        { variant: "error" },
    );

    // ui state
    const [applyToFutureInSeries, setApplyToFutureInSeries] = useState("false");
    const [autoAcceptParticipationForGroup, setAutoAcceptParticipationForGroup] = useState<AudienceGroup>();
    const [bookingRules, setBookingRules] = useState<BookingRules | null>(null);
    const [coaches, setCoaches] = useState<string[]>([]);
    const [customMaxNumParticipants, setCustomMaxNumParticipants] = useState(0);
    const [customMinNumParticipants, setCustomMinNumParticipants] = useState(0);
    const [description, setDescription] = useState("");
    const [privateDescription, setPrivateDescription] = useState("");
    const [detailsDescription, setDetailsDescription] = useState("");
    const [duration, setDuration] = useState(0);
    const [finalizationDeadline, setFinalizationDeadline] = useState<string>();
    const [fundsRecipientChoices, setFundsRecipientChoices] = useState<BookingFundsRecipients["recipientChoices"]>([]);
    const [isLoading, setLoading] = useState(false);
    const [participationRequestType, setParticipationRequestType] = useState<CreateBookingParams["participationRequestType"]>("requestable-by-followers");
    const [participants, setParticipants] = useState<InviteableUser[]>([]);
    const [removeParticipantUserIds, setRemoveParticipantUserIds] = useState<string[]>([]);
    const [showEditRepeatedBookingDialog, setShowEditRepeatedBookingDialog] = useState(false);
    const [sportForm, setSportForm] = useState("");
    const [bookingTags, setBookingTags] = useState<string[]>([]);
    const [bookingTag, setBookingTag] = useState("");
    const [standardParticipationPrice, setStandardParticipationPrice] = useState(0);
    const [startDate, setStartDate] = useState(moment().add(60, "minutes").toISOString());
    const [clubId, setClubId] = useState<string | undefined>();
    const [audienceCount, setAudienceCount] = useState<number>(0);
    const [clubSite, setClubSite] = useState<string | undefined>();
    const typeRules = bookingRules?.availableBookingTypes.find((t) => t.bookingType === booking?.bookingType);
    const [exclusiveCompanyPrice, setExclusiveCompanyPrice] = useState(0);

    // prefill values
    useEffect(() => {
        if (booking) {
            setApplyToFutureInSeries("false");
            setAutoAcceptParticipationForGroup(booking.autoAcceptParticipationForGroup);
            setCustomMaxNumParticipants(booking.customMaxNumParticipants === -1 ? 0 : (booking.customMaxNumParticipants || 0));
            setCustomMinNumParticipants(booking.customMinNumParticipants || 0);
            setDescription(booking.description);
            setPrivateDescription(booking.privateDescription);
            setDetailsDescription(booking.detailsDescription);
            setDuration(moment(booking.endDate).diff(booking.startDate, "minutes"));
            setFinalizationDeadline(booking.finalizationDeadline);
            setParticipationRequestType(booking.participationRequestType);
            setParticipants([]);
            setCoaches([]);
            setBookingTags(booking.bookingTags);
            setRemoveParticipantUserIds([]);
            setSportForm(booking.sportForm);
            setStandardParticipationPrice((booking.standardParticipationPriceCents || 0) / 100);
            setStartDate(booking.startDate);
            setExclusiveCompanyPrice((booking.exclusiveCompanyPriceCents ? booking.exclusiveCompanyPriceCents / 100 : 0));
        }
    }, [booking]);

    // get booking rules on change
    const fetchBookingRules = async () => {
        setBookingRules(await getBookingRules({
            bookingType: booking!.bookingType,
            courtId: booking!.court.id,
            startDate,
        }));
    };
    useEffect(() => { if (booking) fetchBookingRules(); }, [booking, startDate]); // eslint-disable-line
    useEffect(() => { if (booking) onSelectClub(); }, [booking]);
    // fetch funds recipients
    const fetchFundsRecipients = async () => {
        const { recipientChoices } = await patchFundsRecipients({
            courtId: booking!.court.id,
            hideCreator: booking!.hideCreator,
            participants: booking!.participants.map(({ userId, participantRole }) => ({ participantRole, userId })),
        });
        setFundsRecipientChoices(recipientChoices);
    };
    useEffect(() => { if (booking?.fundsRecipientId) fetchFundsRecipients(); }, [booking]);

    const isExlusiveCompanyPrise: boolean = (booking && booking!.exclusiveCompanyClub && options?.eligibleEditingParameters.includes("exclusiveCompanyPriceCents")) || false;

    const onUpdateRequestType = () => {
        if (booking!.bookingType === BookingType.Training && !autoAcceptParticipationForGroup?.clubId && !(club?.capabilities.includes("sip") || club?.capabilities.includes("public-ground") || club?.capabilities.includes("commercial"))) {
            setAutoAcceptParticipationForGroup({ ...autoAcceptParticipationForGroup, clubId: club?.id });
        }
    };

    // api request
    const saveAction = async () => {
        setShowEditRepeatedBookingDialog(false);
        setLoading(true);
        const tags = new Set<string>();
        bookingTags?.forEach((t) => tags.add(t));
        if (bookingTag) {
            tags.add(bookingTag);
        }
        if (bookingTag === "video-stream") {
            tags.delete("video-conference");
        }
        if (bookingTag === "video-conference") {
            tags.delete("video-stream");
        }

        let newParticipantsAudienceGroup: { clubId?: string, clubSite?: string } = {};
        if (booking?.bookingTags.includes("company-exclusive") && options?.possibleOptions.includes("edit") && options?.eligibleEditingParameters.includes("newParticipants")) {
            newParticipantsAudienceGroup = {
                clubId,
                clubSite,
            };
        }
        try {
            await patchBooking({
                applyToFutureInSeries: (booking?.repeatType !== "never") ? (applyToFutureInSeries === "true") : undefined,
                autoAcceptParticipationForGroup: options?.eligibleEditingParameters.includes("autoAcceptParticipationForGroup") ? autoAcceptParticipationForGroup : undefined,
                bookingId: booking!.id,
                customMaxNumParticipants: options?.eligibleEditingParameters.includes("customMaxNumParticipants") ? (customMaxNumParticipants === 0 ? -1 : customMaxNumParticipants) : undefined, // eslint-disable-line
                customMinNumParticipants: options?.eligibleEditingParameters.includes("customMinNumParticipants") ? customMinNumParticipants : undefined,
                description: options?.eligibleEditingParameters.includes("description") ? description : undefined,
                privateDescription: options?.eligibleEditingParameters.includes("privateDescription") ? privateDescription : undefined,
                detailsDescription: options?.eligibleEditingParameters.includes("detailsDescription") ? detailsDescription : undefined,
                durationMins: options?.eligibleEditingParameters.includes("durationMins") ? duration : undefined,
                finalizationDeadline: options?.eligibleEditingParameters.includes("finalizationDeadline") ? finalizationDeadline : undefined,
                newParticipants: options?.eligibleEditingParameters.includes("newParticipants") ? participants.map(({ id }) => ({ participantRole: coaches.includes(id) ? "coach" : "standard", userId: id })) : undefined,
                participationRequestType: options?.eligibleEditingParameters.includes("participationRequestType") ? participationRequestType : undefined,
                removeParticipantUserIds: options?.eligibleEditingParameters.includes("removeParticipantUserIds") ? removeParticipantUserIds : undefined,
                sportForm: options?.eligibleEditingParameters.includes("sportForm") ? sportForm : undefined,
                standardParticipationPriceCents: options?.eligibleEditingParameters.includes("standardParticipationPriceCents") ? standardParticipationPrice * 100 : undefined,
                startDate: options?.eligibleEditingParameters.includes("startDate") ? startDate : undefined,
                bookingTags: (options?.eligibleEditingParameters.includes("bookingTags.video-conference") || options?.eligibleEditingParameters.includes("bookingTags.video-stream")) ? [...tags] : undefined,
                newParticipantsAudienceGroup: newParticipantsAudienceGroup.clubId ? newParticipantsAudienceGroup : undefined,
                exclusiveCompanyPriceCents: isExlusiveCompanyPrise ? exclusiveCompanyPrice * 100 : undefined,
            });
            onFetchBookings();
            getCompanyBookings(club!.id);
            fetchBooking(booking!.id);
            if (moment(booking!.startDate).isSame(startDate, "day")) {
                onCancel();
            } else {
                onClose();
            }
        } catch (error: any) {
            if (error.type === "invalidOrForbiddenParticipants") {
                displayCustomError(error);
            } else {
                displayError(error);
            }
        }
        setLoading(false);
    };

    const onSelectClub = async (id?: string, site?: string) => {
        try {
            setClubId(id);
            setClubSite(site);
            if (id) {
                const audience = await getAdminMemberCount({ clubId: id, bookingId: booking?.id, clubSite: site });
                setAudienceCount(audience.invitedCount);
            } else {
                setAudienceCount(0);
            }
        } catch (error) {
            displayError(error);
        }
    };

    const adminOrOwner: UserRole[] = ["owner", "admin"];
    const isCompanyExclusive = booking?.bookingTags.includes("company-exclusive");
    const isEditable = options?.possibleOptions.includes("edit");
    const isEligibleEditingNewParticipants = options?.eligibleEditingParameters.includes("newParticipants");

    // render
    const detailsDescriptionLength = config.bookings.getStrippedLength(detailsDescription || "");
    return bookingRules && (
        <>
            <Dialog
                fullWidth
                open={show}
                onClose={onClose}
            >
                <DialogTitle>{i18n.editBooking}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>

                        {/* start date */}
                        <Grid item xs={6}>
                            <StartDatePicker
                                bookingRules={bookingRules}
                                duration={duration}
                                pickerProps={{
                                    disabled: !options?.eligibleEditingParameters.includes("startDate"),
                                    fullWidth: true,
                                    inputVariant: "outlined",
                                    label: i18n.startDate,
                                    variant: "dialog",
                                }}
                                startDate={startDate}
                                onChange={setStartDate}
                            />
                        </Grid>

                        {/* duration */}
                        <Grid item xs={6}>
                            <DurationPicker
                                bookingRules={bookingRules}
                                disabled={!options?.eligibleEditingParameters.includes("durationMins")}
                                duration={duration}
                                onChange={setDuration}
                            />
                        </Grid>

                        {/* title & desc */}
                        {typeRules?.requiresDescription && (
                            <>
                                <GridTextField
                                    disabled={!options?.eligibleEditingParameters.includes("description")}
                                    helperText={i18n.titleLength}
                                    label={i18n.title}
                                    maxLength={40}
                                    size={12}
                                    value={description}
                                    onChange={setDescription}
                                />
                                <GridTextField
                                    disabled={!options?.eligibleEditingParameters.includes("detailsDescription")}
                                    error={detailsDescriptionLength > config.bookings.detailsDescriptionMaxLength}
                                    helperText={detailsDescriptionLength > config.bookings.detailsDescriptionMaxLength - 28 ? `${detailsDescriptionLength} / ${config.bookings.detailsDescriptionMaxLength}` : undefined}
                                    label={i18n.description}
                                    maxLength={config.bookings.detailsDescriptionAbsoluteMaxLength}
                                    multiline
                                    size={12}
                                    value={detailsDescription}
                                    onChange={setDetailsDescription}
                                />
                            </>
                        )}

                        {/* privateDescription */}
                        <GridTextField
                            disabled={!options?.eligibleEditingParameters.includes("privateDescription")}
                            label={i18n.privateDescription}
                            maxLength={255}
                            multiline
                            size={12}
                            value={privateDescription}
                            onChange={setPrivateDescription}
                        />

                        {/* min / max num participants */}
                        {options?.eligibleEditingParameters.includes("customMinNumParticipants") && (
                            <Grid item xs={6}>
                                <NumberPicker
                                    helperText={customMinNumParticipants !== (booking?.customMinNumParticipants || 0) ? i18n.editCustomMinNumParticipants : undefined}
                                    label={i18n.minNumParticipants}
                                    min={(booking?.bookingType === BookingType.Standard) ? 2 : 0}
                                    value={customMinNumParticipants}
                                    onChange={setCustomMinNumParticipants}
                                />
                            </Grid>
                        )}
                        {options?.eligibleEditingParameters.includes("customMaxNumParticipants") && (
                            <Grid item xs={6}>
                                <NumberPicker
                                    helperText={customMaxNumParticipants !== (booking?.customMaxNumParticipants === -1 ? 0 : (booking?.customMaxNumParticipants || 0)) ? i18n.editCustomMaxNumParticipants : undefined}
                                    label={i18n.maxNumParticipants}
                                    min={0}
                                    value={customMaxNumParticipants}
                                    zeroPlaceholder="&infin;" // infinity symbol
                                    onChange={setCustomMaxNumParticipants}
                                />
                            </Grid>
                        )}

                        {/* remove pending participants */}
                        {typeRules?.allowsParticipants && options?.eligibleEditingParameters.includes("removeParticipantUserIds") && booking?.participants.find((p) => p.invitationStatus === "pending") && (
                            <Grid item xs={12}>
                                <FormControl fullWidth variant="outlined">
                                    <Autocomplete
                                        clearText={i18n.reset}
                                        forcePopupIcon={false}
                                        multiple
                                        open={false}
                                        options={booking?.participants || []}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                InputProps={{ ...params.InputProps, readOnly: true }}
                                                label={i18n.removePendingParticipants}
                                                variant="outlined"
                                            />
                                        )}
                                        renderTags={(value, getTagProps) => value.map((option, index) => (
                                            <Chip
                                                avatar={<Avatar src={option.user.profilePictureUrl} />}
                                                label={`${option.user.firstName} ${option.user.lastName}`}
                                                {...getTagProps({ index })}
                                                onDelete={() => setRemoveParticipantUserIds([...removeParticipantUserIds, option.userId])}
                                            />
                                        ))}
                                        value={booking?.participants.filter((p) => p.invitationStatus === "pending" && !removeParticipantUserIds.includes(p.userId)) || []}
                                        onInputChange={(e, v, r) => (r === "clear") && setRemoveParticipantUserIds([])}
                                    />
                                </FormControl>
                            </Grid>
                        )}

                        {/* Invite organization members */}
                        {booking && isCompanyExclusive && isEditable && isEligibleEditingNewParticipants && (
                            <>
                                <Grid item xs={12}>
                                    {booking.exclusiveCompanyClub ? (
                                        <ClubSelect
                                            clubFilter={(c) => c.id === booking.exclusiveCompanyClub?.id}
                                            clubId={clubId}
                                            controlProps={{ fullWidth: true }}
                                            userRoles={adminOrOwner}
                                            onChange={(id?: string) => onSelectClub(id, clubSite)}
                                            hideAll
                                            showNone
                                            customLabel={i18n.organisationMembers}
                                        />
                                    ) : (
                                        <ClubSelect
                                            clubFilter={(c) => c.capabilities.includes("company")}
                                            clubId={clubId}
                                            controlProps={{ fullWidth: true }}
                                            userRoles={adminOrOwner}
                                            onChange={(id?: string) => onSelectClub(id, clubSite)}
                                            hideAll
                                            showNone
                                            customLabel={i18n.organisationMembers}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body1" style={{ marginTop: 20 }}>{i18n.audienceSize(audienceCount)}</Typography>
                                </Grid>
                            </>
                        )}

                        {/* (new) participants, participation requests */}
                        {typeRules?.allowsParticipants && (
                            <Grid item xs={12}>
                                <ParticipantsPicker
                                    coaches={coaches}
                                    court={booking!.court.id}
                                    disabled={!isEligibleEditingNewParticipants}
                                    excludeUsers={booking?.participants.map((p) => p.userId)}
                                    label={i18n.newParticipants}
                                    participants={participants}
                                    onChange={(p, c) => { setParticipants(p); setCoaches(c); }}
                                />
                            </Grid>
                        )}

                        {booking && isCompanyExclusive && isEditable && isEligibleEditingNewParticipants && (
                            <Grid item xs={12}>
                                <GridTextField
                                    label={i18n.clubSite}
                                    maxLength={255}
                                    size={12}
                                    value={clubSite || ""}
                                    onChange={(site: string) => onSelectClub(clubId, site)}
                                />
                            </Grid>
                        )}

                        {/* exclusive Company Price */}
                        {isExlusiveCompanyPrise && (
                            <Grid item xs={6}>
                                <CurrencyTextField
                                    currencySymbol="€"
                                    decimalCharacter=","
                                    digitGroupSeparator="."
                                    fullWidth
                                    label={i18n.exclusiveCompanyPriceCentsLbl}
                                    helperText={exclusiveCompanyPrice > 50 ? i18n.standardParticipationPriceCentsWarn : ""}
                                    maximumValue="100"
                                    minimumValue="0"
                                    value={exclusiveCompanyPrice}
                                    variant="outlined"
                                    onChange={(e: any, value: number) => setExclusiveCompanyPrice(value)}
                                />
                            </Grid>
                        )}

                        {/* Participation requests */}
                        {typeRules?.allowsParticipants && (
                            <>
                                <Grid item xs={6}>
                                    <FormControl fullWidth variant="outlined" disabled={!options?.eligibleEditingParameters.includes("participationRequestType")}>
                                        <InputLabel>{i18n.participationRequests}</InputLabel>
                                        <Select
                                            label={i18n.participationRequests}
                                            value={participationRequestType}
                                            onChange={(e) => {
                                                setParticipationRequestType(e.target.value as typeof participationRequestType);
                                                if (e?.target?.value === "auto-accept-for-group") {
                                                    onUpdateRequestType();
                                                }
                                            }}
                                        >
                                            {["requestable-by-followers", "invite-only", "auto-accept"].map((t) => (
                                                <MenuItem key={t} value={t}>{i18n.participationRequestType[t]}</MenuItem>
                                            ))}
                                            {(booking?.bookingType === BookingType.Training) && (
                                                <MenuItem value="auto-accept-for-group">{i18n.participationRequestType["auto-accept-for-group"]}</MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {(participationRequestType === "auto-accept-for-group" && options?.eligibleEditingParameters.includes("autoAcceptParticipationForGroup")) && (
                                    <Grid item xs={12}>
                                        <AudienceSelect
                                            defaultValue={autoAcceptParticipationForGroup}
                                            onChange={setAutoAcceptParticipationForGroup}
                                        />
                                    </Grid>
                                )}
                            </>
                        )}

                        {/* finalization deadline */}
                        {options?.eligibleEditingParameters.includes("finalizationDeadline") && (
                            <Grid item xs={6}>
                                <DateSelect
                                    disablePast
                                    initialFocusedDate={startDate}
                                    label={i18n.finalizationDeadline}
                                    value={finalizationDeadline}
                                    variant="outlined"
                                    withTime
                                    onChange={(m) => setFinalizationDeadline(m?.set({ seconds: 0, milliseconds: 0 }).toISOString())}
                                />
                            </Grid>
                        )}

                        {/* participation fee */}
                        {options?.eligibleEditingParameters.includes("standardParticipationPriceCents") && (
                            <Grid item xs={6}>
                                <CurrencyTextField
                                    disabled={isCompanyExclusive}
                                    currencySymbol="€"
                                    decimalCharacter=","
                                    digitGroupSeparator="."
                                    fullWidth
                                    label={i18n.standardParticipationPriceCents}
                                    helperText={standardParticipationPrice > 50 ? i18n.standardParticipationPriceCentsWarn : ""}
                                    maximumValue="100"
                                    minimumValue="0"
                                    value={standardParticipationPrice}
                                    variant="outlined"
                                    onChange={(e: any, value: number) => setStandardParticipationPrice(value)}
                                />
                            </Grid>
                        )}

                        {/* funds recipient */}
                        {!isCompanyExclusive && booking?.fundsRecipientId && (
                            <Grid item xs={6}>
                                <FormControl disabled fullWidth variant="outlined">
                                    <InputLabel>{i18n.fundsRecipient}</InputLabel>
                                    <Select label={i18n.fundsRecipient} value={booking.fundsRecipientId}>
                                        {fundsRecipientChoices.map((c) => (
                                            <MenuItem key={c.id} value={c.id}>{c.displayName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {/* sport form */}
                        {booking?.bookingType === BookingType.Training && options?.eligibleEditingParameters.includes("sportForm") && (
                            <Grid item xs={6}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>{i18n.sportForm}</InputLabel>
                                    <Select
                                        label={i18n.sportForm}
                                        value={sportForm}
                                        onChange={(e) => setSportForm(e.target.value as string)}
                                    >
                                        {booking.court.sportForms.map((t) => (
                                            <MenuItem key={t} value={t}>{uI18n.sportForms[t]}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        {/* booking tag */}
                        {(options?.eligibleEditingParameters.includes("bookingTags.video-conference") || options?.eligibleEditingParameters.includes("bookingTags.video-stream")) && (
                            <Grid item xs={6}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel>{i18n.bookingTags}</InputLabel>
                                    <Select
                                        label={i18n.bookingTags}
                                        value={bookingTag}
                                        onChange={(e) => setBookingTag(e.target.value as string)}
                                    >
                                        {["video-stream", "video-conference"].map((t) => (
                                            <MenuItem key={t} value={t}>{i18n.virtualEvent[t]}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                    </Grid>
                </DialogContent>
                {isLoading && <LinearProgress />}
                <DialogActions>
                    <Button color="secondary" onClick={onCancel}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" disabled={isLoading} onClick={(booking?.repeatType !== "never") ? () => setShowEditRepeatedBookingDialog(true) : saveAction}>
                        {i18n.save}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* edit repeated booking dialog */}
            <Dialog
                open={showEditRepeatedBookingDialog}
                onClose={() => setShowEditRepeatedBookingDialog(false)}
            >
                <DialogTitle>{i18n.editRepeatedBooking}</DialogTitle>
                <DialogContent dividers>
                    <RadioGroup value={applyToFutureInSeries} onChange={(e) => setApplyToFutureInSeries(e.target.value)}>
                        <FormControlLabel value="false" control={<Radio color="primary" />} label={i18n.thisBooking} />
                        <FormControlLabel value="true" control={<Radio color="primary" />} label={i18n.thisBookingAndFollowing} />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button color="secondary" onClick={() => setShowEditRepeatedBookingDialog(false)}>
                        {i18n.cancel}
                    </Button>
                    <Button color="primary" onClick={saveAction}>
                        {i18n.ok}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditBookingDialog;
